import { useState } from "react";
import { Loader2, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { trackContact, trackLead } from "@/lib/tracking";

const EMPTY = {
  name: "",
  phone: "",
  email: "",
  wedding_date: "",
  wedding_location: "",
  venue_status: "",
  guest_count: "",
  overall_budget: "",
  planning_budget: "",
  message: "",
};

const VENUE_STATUS_OPTIONS = [
  "Yes",
  "No",
  "Shortlisting Options",
  "Need Help Finding One",
];

const GUEST_OPTIONS = [
  "Under 100",
  "100\u2013200",
  "200\u2013400",
  "400\u2013600",
  "600+",
];

const OVERALL_BUDGET_OPTIONS = [
  "Under \u20B910 Lakhs",
  "\u20B910\u201320 Lakhs",
  "\u20B920\u201335 Lakhs",
  "\u20B935\u201350 Lakhs",
  "\u20B950\u201375 Lakhs",
  "\u20B975 Lakhs\u20131 Crore",
  "\u20B91 Crore+",
];

const PLANNING_BUDGET_OPTIONS = [
  "Under \u20B91 Lakh",
  "\u20B91\u20132 Lakhs",
  "\u20B92\u20134 Lakhs",
  "\u20B94\u20136 Lakhs",
  "\u20B96\u201310 Lakhs",
  "\u20B910 Lakhs+",
];

export default function Contact({ data, footer }) {
  const label = data?.label || "Contact";
  const headline = data?.headline || "Tell us about your wedding.";
  const subheadline = data?.subheadline || "";
  const responseTime = data?.response_time || "Within 24\u201348 Hours";
  const submitLabel = data?.submit_label || "Schedule My Consultation";
  const successTitle = data?.success_title || "Thank you for reaching out.";
  const successMessage = data?.success_message || "A member of our planning team will get in touch within 24\u201348 hours.";

  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share your name, phone and email.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, source: "website" };
      await api.post("/leads", payload);
      trackLead(payload);
      setDone(true);
      setForm(EMPTY);
      toast.success("Inquiry sent. We'll be in touch within 24\u201348 hours.");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail, "Could not send. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const waHref = footer?.whatsapp_number
    ? `https://wa.me/${footer.whatsapp_number}?text=${encodeURIComponent(footer.whatsapp_message || "Hi Weddings by Mostash, I'm planning my wedding and would love to know how you can help.")}`
    : null;

  return (
    <section id="contact" data-testid="contact-section" className="bg-ivory py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="grid grid-cols-12 gap-10 md:gap-16">
          <div className="col-span-12 lg:col-span-5">
            <div className="reveal flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-gold" />
              <span className="eyebrow">{label}</span>
            </div>
            <h2 data-testid="contact-headline" className="reveal font-display text-[2.25rem] leading-[1.05] sm:text-5xl md:text-6xl tracking-tight text-charcoal">
              {headline}
            </h2>
            <p className="reveal mt-5 text-charcoal/70 font-light leading-relaxed max-w-md">{subheadline}</p>

            <div className="reveal mt-6 inline-flex items-center gap-2 text-teal-600 border border-teal-600/30 px-3 py-2 bg-sage/30">
              <Clock className="h-3.5 w-3.5" strokeWidth={1.6} />
              <span className="uppercase tracking-[0.22em] text-[10px] font-medium">Response time &middot; {responseTime}</span>
            </div>

            {footer ? (
              <div className="reveal mt-10 space-y-6 text-charcoal/80 font-light">
                {footer.phone ? (
                  <div>
                    <div className="uppercase tracking-[0.25em] text-[10px] text-charcoal/50 mb-1">Call us</div>
                    <a href={`tel:${footer.phone}`} onClick={() => trackContact("phone", { source_section: "contact_panel" })} data-testid="contact-info-phone" className="font-display text-2xl text-charcoal hover:text-teal-600">{footer.phone}</a>
                  </div>
                ) : null}
                {footer.email ? (
                  <div>
                    <div className="uppercase tracking-[0.25em] text-[10px] text-charcoal/50 mb-1">Write to us</div>
                    <a href={`mailto:${footer.email}`} onClick={() => trackContact("email", { source_section: "contact_panel" })} data-testid="contact-info-email" className="font-display text-2xl text-charcoal hover:text-teal-600">{footer.email}</a>
                  </div>
                ) : null}
                {waHref ? (
                  <div>
                    <div className="uppercase tracking-[0.25em] text-[10px] text-charcoal/50 mb-1">Or message us</div>
                    <a href={waHref} target="_blank" rel="noopener noreferrer" onClick={() => trackContact("whatsapp", { source_section: "contact_panel" })} data-testid="contact-info-whatsapp" className="font-display text-xl text-charcoal hover:text-teal-600">
                      WhatsApp &rarr;
                    </a>
                  </div>
                ) : null}
                <div>
                  <div className="uppercase tracking-[0.25em] text-[10px] text-charcoal/50 mb-1">Studios</div>
                  <div className="text-charcoal/80">{(footer.offices || []).map((o) => o.city).join("  \u00B7  ")}</div>
                </div>
              </div>
            ) : null}
          </div>

          <div className="col-span-12 lg:col-span-7">
            {done ? (
              <div data-testid="contact-success" className="reveal min-h-[420px] bg-white border border-beige p-10 md:p-14 flex flex-col justify-center">
                <div className="uppercase tracking-[0.3em] text-[10px] text-charcoal/50 mb-4">Inquiry received</div>
                <h3 className="font-display text-3xl md:text-4xl text-charcoal">{successTitle}</h3>
                <p className="mt-5 text-charcoal/70 font-light max-w-md leading-relaxed">{successMessage}</p>
                <div className="mt-8 flex flex-wrap gap-3">
                  {waHref ? (
                    <a
                      href={waHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => trackContact("whatsapp", { source_section: "contact_success" })}
                      data-testid="contact-success-whatsapp"
                      className="inline-flex items-center gap-2 h-11 px-5 bg-[#25D366] text-white text-[11px] uppercase tracking-[0.22em] font-medium hover:bg-charcoal transition-colors"
                    >
                      WhatsApp us
                    </a>
                  ) : null}
                  <button
                    type="button"
                    onClick={() => setDone(false)}
                    data-testid="contact-send-another"
                    className="text-[11px] uppercase tracking-[0.22em] border-b border-charcoal/60 pb-1 hover:text-teal-600 hover:border-teal-600 transition-colors"
                  >
                    Send another inquiry
                  </button>
                </div>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="reveal bg-white border border-beige p-6 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2"
              >
                <Field label="Full Name" name="name" value={form.name} onChange={onChange} required testid="contact-input-name" />
                <Field label="Mobile Number" name="phone" type="tel" value={form.phone} onChange={onChange} required testid="contact-input-phone" />
                <Field label="Email Address" name="email" type="email" value={form.email} onChange={onChange} required testid="contact-input-email" />
                <Field label="Wedding Date" name="wedding_date" placeholder="e.g. December 2026" value={form.wedding_date} onChange={onChange} testid="contact-input-date" />
                <div className="md:col-span-2">
                  <Field label="Wedding Location" name="wedding_location" placeholder="e.g. Goa, India" value={form.wedding_location} onChange={onChange} testid="contact-input-location" />
                </div>
                <SelectField label="Have You Finalised Your Venue?" name="venue_status" value={form.venue_status} onChange={onChange} options={VENUE_STATUS_OPTIONS} testid="contact-input-venue-status" />
                <SelectField label="Estimated Guest Count" name="guest_count" value={form.guest_count} onChange={onChange} options={GUEST_OPTIONS} testid="contact-input-guests" />
                <SelectField label="Overall Wedding Budget" name="overall_budget" value={form.overall_budget} onChange={onChange} options={OVERALL_BUDGET_OPTIONS} testid="contact-input-overall-budget" />
                <SelectField label="Planning Budget" name="planning_budget" value={form.planning_budget} onChange={onChange} options={PLANNING_BUDGET_OPTIONS} testid="contact-input-planning-budget" />
                <div className="md:col-span-2 mt-6">
                  <label className="uppercase tracking-[0.25em] text-[10px] text-charcoal/50">Tell us about your vision?</label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={onChange}
                    rows={4}
                    data-testid="contact-input-message"
                    placeholder="Tell us a little about your wedding, be creative or bizzare. We want to know."
                    className="mt-3 field-textarea"
                  />
                </div>
                <div className="md:col-span-2 mt-10">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="contact-submit"
                    className="group btn-primary disabled:opacity-60 disabled:cursor-not-allowed bg-charcoal hover:bg-teal-600"
                  >
                    {submitting ? (
                      <><Loader2 className="h-4 w-4 animate-spin" />Sending</>
                    ) : (
                      <>{submitLabel}<Send className="h-4 w-4 transition-transform group-hover:translate-x-1" strokeWidth={1.5} /></>
                    )}
                  </button>
                  <p className="mt-4 text-[11px] tracking-wide text-charcoal/40 font-light">We respond personally within 24&ndash;48 hours.</p>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, type = "text", value, onChange, required, placeholder, testid }) {
  return (
    <label className="block py-3">
      <span className="uppercase tracking-[0.25em] text-[10px] text-charcoal/50">{label}{required ? " *" : ""}</span>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-3 field-input"
      />
    </label>
  );
}

function SelectField({ label, name, value, onChange, options, testid }) {
  return (
    <label className="block py-3">
      <span className="uppercase tracking-[0.25em] text-[10px] text-charcoal/50">{label}</span>
      <select name={name} value={value} onChange={onChange} data-testid={testid} className="mt-3 field-input bg-white">
        <option value="">Select&hellip;</option>
        {options.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    </label>
  );
}
